import { useMemo } from 'react';
import { EnrichedEmployee, OrgStructure } from '../../Structure/types';
import { EmployeeEventMap } from './useAbsenceEventBuckets';

// --- Types ---

export type StaffingWarningRule = 'no_staff' | 'low_staff';

export interface DepartmentStaffingWarning {
  date: string;
  department_id: string;
  department_title: string;
  total_employees: number;
  present_employees: number;
  absent_employees: EnrichedEmployee[];
  rule: StaffingWarningRule;
}

// --- Helpers ---

/**
 * Generates an array of YYYY-MM-DD strings between start and end inclusively.
 */
function getDatesInRange(startStr: string, endStr: string): string[] {
  const dates: string[] = [];
  const current = new Date(startStr);
  const end = new Date(endStr);

  while (current <= end) {
    dates.push(current.toISOString().split('T')[0]);
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

// --- Hook ---

export function useDepartmentStaffingWarnings(
  orgStructure: OrgStructure | undefined,
  eventsMap: EmployeeEventMap | undefined,
  startDate: string,
  endDate: string
): DepartmentStaffingWarning[] {
  
  return useMemo(() => {
    if (!orgStructure || !eventsMap || !startDate || !endDate) {
      return [];
    }

    const warnings: DepartmentStaffingWarning[] = [];
    const datesToCheck = getDatesInRange(startDate, endDate);

    // 1. Filter down to departments that matter (>= 2 employees)
    const targetDepartments = orgStructure.departments.filter(
      (dept) => dept.employees && dept.employees.length >= 2
    );

    // 2. Helper to check if an employee is absent on a specific YYYY-MM-DD
    const isAbsentOnDate = (employeeId: string, targetDate: string): boolean => {
      // O(1) lookup using the date string as the key
      const eventForDate = eventsMap[employeeId]?.[targetDate];
      
      // Consider them absent if the event exists and wasn't cancelled
      return !!eventForDate && !eventForDate.cancelled_at;
    };

    // 3. Scan the matrix: Dates x Departments
    for (const date of datesToCheck) {
      // Optional: Add `const day = new Date(date).getDay(); if (day === 0 || day === 6) continue;` to skip weekends

      for (const dept of targetDepartments) {
        const employees = dept.employees!;
        const totalStaff = employees.length;
        
        // Find who is absent via the bucketed map
        const absentEmployees = employees.filter((emp) => isAbsentOnDate(emp._id, date));
        const absentCount = absentEmployees.length;
        const presentCount = totalStaff - absentCount;

        // Apply Rule 1: <= 3 people total -> warning if 0 people present
        if (totalStaff <= 3) {
          if (presentCount === 0) {
            warnings.push({
              date,
              department_id: dept._id,
              department_title: dept.title,
              total_employees: totalStaff,
              present_employees: presentCount,
              absent_employees: absentEmployees,
              rule: 'no_staff',
            });
          }
        } 
        // Apply Rule 2: > 3 people total -> warning if < 2 people present
        else {
          if (presentCount < 2) {
            warnings.push({
              date,
              department_id: dept._id,
              department_title: dept.title,
              total_employees: totalStaff,
              present_employees: presentCount,
              absent_employees: absentEmployees,
              rule: 'low_staff',
            });
          }
        }
      }
    }

    return warnings;
  }, [orgStructure, eventsMap, startDate, endDate]);
}
